/**
 * Class Name Merger Utility
 * Combines clsx and tailwind-merge for intelligent class merging
 *
 * @description Merges Tailwind CSS classes while handling conflicts properly
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge class names with Tailwind conflict resolution
 *
 * Combines multiple class values and resolves Tailwind CSS conflicts.
 * Later classes override earlier ones when they affect the same property.
 *
 * @param inputs - Class values (strings, arrays, objects, etc.)
 * @returns Merged class string with conflicts resolved
 *
 * @example
 * ```tsx
 * // Basic usage
 * cn('px-2 py-1', 'px-4')
 * // => 'py-1 px-4'
 *
 * // Conditional classes
 * cn('base-class', isActive && 'active', disabled && 'opacity-50')
 *
 * // With objects
 * cn('base', { 'text-error-vivid': hasError, 'text-success-vivid': isSuccess })
 *
 * // Component variants
 * cn(
 *   'rounded-md font-medium',
 *   variant === 'primary' && 'bg-info-vivid text-white',
 *   variant === 'secondary' && 'bg-elevated text-main',
 *   className // Allow override from props
 * )
 * ```
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export default cn;
